import { getParameterByName } from "@src/utils/getQuery";
import "./pagination.css";
import { LIST_TYPE } from "./renderListMap";
import { request } from "@src/utils/request";
import { reRenderFramerProfile } from "../farmerProfile/farmerProfile";
import { IContext } from "@src/main";

const ACTIVE_PAGE_CLASS = "container-pagination-page__current";
const NORMAL_PAGE_CLASS = "container-pagination-page";
const UNAVAILABLE_ACTION_CLASS = "container-pagination-action__unavailable";
const PAGE_SIZE = 12;

export default function setupPagination(context: IContext) {
  const listContainerWithListType = document.querySelector(
    "[data-list-type]"
  ) as HTMLElement;
  const paginationWrap = document.querySelector(
    ".container-pagination"
  ) as HTMLDivElement;

  if (!listContainerWithListType || !paginationWrap) return;
  const listType = listContainerWithListType.dataset.listType as
    | keyof typeof LIST_TYPE
    | undefined;
  if (!listType || !LIST_TYPE[listType]) return;

  const listTypeItem = LIST_TYPE[listType];
  const currentPage = getParameterByName("page") || "1";
  const total = paginationWrap.dataset.totalPage;

  if (!total) return;

  // The content of the first page is rendered by go template
  if (currentPage !== "1") {
    updatePage(currentPage);
  }

  paginationWrap.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    const closestGotoElement = target.closest("[data-goto]") as HTMLElement;
    const closestAvailableGotoElement = target.closest(
      ".container-pagination-action__unavailable"
    ) as HTMLElement;
    if (!closestGotoElement || closestAvailableGotoElement) return;

    const goTo = closestGotoElement.dataset.goto;
    if (!goTo) return;

    switch (goTo) {
      case "first":
        goPage(1);
        break;
      case "last":
        goPage(Number(total));
        break;
      case "next":
        if (currentPage === total) return;
        goPage(Number(currentPage) + 1);
        break;
      case "previous":
        if (currentPage === "1") return;
        goPage(Number(currentPage) - 1);
        break;
      default:
        goPage(Number(goTo));
        break;
    }
  });

  function updatePage(page: string) {
    renderPagination(page);
    renderList(page);
  }

  async function renderList(page: string) {
    const ulEle = listContainerWithListType?.querySelector("ul");
    if (!ulEle) return;
    // clear page loading
    ulEle.innerHTML = "";

    let list;
    if (listType === "farmer-blog-list") {
      list = await fetchFarmerBlogJSON(Number(page));
    } else {
      list = await fetchList(Number(page));
    }
    if (!list || list.length < 1) return;

    const htmlTemplate = listTypeItem.htmlTemplate(list);

    ulEle.innerHTML = htmlTemplate;
  }

  function renderPagination(page: string) {
    const numberPage = Number(page);
    const numberTotal = Number(total);
    if (isNaN(numberPage) || isNaN(numberTotal) || numberPage === 1) return;

    // clear page 1 status
    const onePagination = paginationWrap.querySelector("[data-goto='1']");
    onePagination?.classList.remove(ACTIVE_PAGE_CLASS);
    paginationWrap
      .querySelectorAll(`.${UNAVAILABLE_ACTION_CLASS}`)
      .forEach((item) => item.classList.remove(UNAVAILABLE_ACTION_CLASS));

    const pageNumberList = [numberPage];

    let i = 1,
      j = 1;
    while (pageNumberList.length < 5) {
      if (numberPage + i <= numberTotal) {
        pageNumberList.push(numberPage + i);
        i++;
        continue;
      }
      if (numberPage - j > 0) {
        pageNumberList.unshift(numberPage - j);
        j++;
        continue;
      }
      break;
    }

    const ulElement = paginationWrap.querySelector("ul.flex");
    if (!ulElement) return;
    ulElement.innerHTML = "";

    const fragment = document.createDocumentFragment();

    pageNumberList.forEach((pageNumber) => {
      const li = document.createElement("li");
      const stringIndex = String(pageNumber);
      li.textContent = stringIndex;
      li.setAttribute("data-goto", stringIndex);
      li.classList.add(NORMAL_PAGE_CLASS);

      if (pageNumber === numberPage) {
        li.classList.add(ACTIVE_PAGE_CLASS);
      }
      fragment.appendChild(li);
    });
    ulElement.append(fragment);

    // disable last page
    if (numberPage === numberTotal) {
      paginationWrap
        .querySelector("[data-goto='next']")
        ?.classList.add(UNAVAILABLE_ACTION_CLASS);
      paginationWrap
        .querySelector("[data-goto='last']")
        ?.classList.add(UNAVAILABLE_ACTION_CLASS);
    }
  }

  function goPage(page: number) {
    const currentUrl = new URL(window.location.href);
    const searchParams = currentUrl.searchParams;
    searchParams.set("page", String(page));
    currentUrl.search = searchParams.toString();
    window.location.href = currentUrl.toString();
  }

  async function fetchList(page: number) {
    return request(listTypeItem.api)
      .then((res) => res?.json())
      .then((list) => {
        if (!Array.isArray(list)) return [];
        const offset = (page - 1) * PAGE_SIZE;
        return list.slice(offset, offset + PAGE_SIZE);
      })
      .catch((error) =>
        context.errorNotifier.notify({
          error,
          msg: `handle ${listTypeItem.api} list failed`,
        })
      );
  }

  async function fetchFarmerBlogJSON(page: number) {
    const username = document.querySelector<HTMLElement>(
      ".container-farmer-profile"
    )?.dataset.farmerUsername;
    if (!username) return Promise.resolve([]);

    return request(`${listTypeItem.api}-${username}.json`)
      .then((res) => res?.json())
      .then((response) => {
        if (response.farmer) {
          reRenderFramerProfile(response.farmer);
        }

        const list = response.blogs;
        if (!Array.isArray(list)) return [];
        const offset = (page - 1) * PAGE_SIZE;
        return list.slice(offset, offset + PAGE_SIZE);
      })
      .catch((error) =>
        context.errorNotifier.notify({
          error,
          msg: `handle farmerBlogJSON list failed`,
        })
      );
  }
}
